import { AnimatePresence, motion } from "motion/react";
import { useMemo } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.min.css";
import clsx from "clsx";
import { cn } from "@/core/helpers/cn";
import useAppSelector from "@/core/helpers/useAppSelector";
import useSetLayer from "@/core/helpers/useSetLayer";
import { Layer } from "@/domains/chat/constants/layer";
import {
  chatIsActiveSelector,
  chatIsExpandedSelector,
  chatIsOpenSelector,
  chatisShowSelector,
  messageIsCopiedSelector,
  searchIsOpenSelector,
  settingsIsOpenSelector,
} from "@/domains/chat/slice";
import toastifyConfig from "@/domains/chat/helpers/toastifyConfig";
import useChatSubscriber from "@/domains/chat/hooks/useChatSubscriber";
import useKeyboardEvent from "@/domains/chat/hooks/useKeyboardEvent";
import useOpacityMode from "@/domains/chat/hooks/useOpacityMode";
import useSendEnabledStatus from "@/domains/chat/hooks/useSendEnabledStatus";
import useTooltipForInput from "@/domains/chat/hooks/useTooltipForInput";
import ChatInputContainer from "@/domains/chat/containers/chat/ChatInputContainer";
import ChatMessagesContainer from "@/domains/chat/containers/chat/ChatMessagesContainer";
import ChatSearchContainer from "@/domains/chat/containers/chat/ChatSearchContainer";
import ChatTabsContainer from "@/domains/chat/containers/chat/ChatTabsContainer";
import ChatCloseButtonContainer from "@/domains/chat/containers/chat/controls/ChatCloseButtonContainer";
import ChatExpandedToggleContainer from "@/domains/chat/containers/chat/controls/ChatExpandedToggleContainer";
import ChatOpenButtonContainer from "@/domains/chat/containers/chat/controls/ChatOpenButtonContainer";
import ChatSettingsOpenToggleContainer from "@/domains/chat/containers/chat/controls/ChatSettingsOpenToggleContainer";
import SettingsContainer from "@/domains/chat/containers/settings/SettingsContainer";
import ChatBody from "@/domains/chat/components/chat/ChatBody";
import ChatHeader from "@/domains/chat/components/chat/ChatHeader";
import ChatWrapper from "@/domains/chat/components/chat/ChatWrapper";
import MessageIsCopied from "@/domains/chat/components/notification/MessageIsCopied";
import { appliedBasicSettingsSelector } from "@/domains/settings/basic/slice";
import useInitChatSettings from "@/domains/settings/chat/hooks/useChatSettings";
import { appliedChatSettingsSelector } from "@/domains/settings/chat/slice";

type Props = {
  className?: string;
};

// TODO: не работают клики в чате: на открытие настроек, закрытие, сворачиваение
function ChatContainer({ className }: Props) {
  const appliedBasicSettings = useAppSelector(appliedBasicSettingsSelector);
  useChatSubscriber();
  useSetLayer(Layer.CHAT);
  useInitChatSettings();
  useKeyboardEvent();
  useSendEnabledStatus();
  useTooltipForInput();
  const { turnOffOpacityMode, isOpacityMode, onMouseEnterHandler, onMouseLeaveHandler } =
    useOpacityMode();
  const isCopied = useAppSelector(messageIsCopiedSelector);
  const isExpanded = useAppSelector(chatIsExpandedSelector);
  const isActive = useAppSelector(chatIsActiveSelector);
  // const isEnabled = useAppSelector(chatIsEnabledSelector);
  const isOpen = useAppSelector(chatIsOpenSelector);
  const isShow = useAppSelector(chatisShowSelector);
  const isOpenSettings = useAppSelector(settingsIsOpenSelector);
  const isOpenSearch = useAppSelector(searchIsOpenSelector);
  const { chatWidthSize, chatHeightSize, fontSize, notifyInRolledUpMode } = useAppSelector(
    appliedChatSettingsSelector,
  );

  const showInput = useMemo(() => !isOpacityMode && isActive, [isOpacityMode, isActive]);
  const showSearch = useMemo(() => showInput && isOpenSearch, [showInput, isOpenSearch]);

  if (!appliedBasicSettings) return null;
  if (!isShow) return null;

  return (
    <>
      <div
        className={cn(
          "fixed left-[--boundary-offset-x] inset-y-[--boundary-offset-y] z-1 ",
          className,
        )}
      >
        <AnimatePresence>
          {isOpen ? (
            <motion.div
              initial={{
                opacity: 0,
              }}
              animate={{
                opacity: 1,
              }}
              exit={{ opacity: 0 }}
              transition={{ type: "spring", duration: 0.2 }}
              className="h-full flex items-start gap-2.5"
            >
              <ChatWrapper
                isExpanded={isExpanded}
                height={chatHeightSize}
                width={chatWidthSize}
                onMouseMove={turnOffOpacityMode}
                onMouseEnter={onMouseEnterHandler}
                onMouseLeave={onMouseLeaveHandler}
              >
                <ChatBody>
                  <ChatHeader isOpacityMode={isOpacityMode}>
                    <ChatTabsContainer />
                    <AnimatePresence>
                      <motion.div
                        initial={{
                          opacity: 0,
                        }}
                        animate={{
                          opacity: 1,
                        }}
                        exit={{
                          opacity: 0,
                        }}
                        transition={{ type: "spring", duration: 0.2 }}
                        className={clsx("ml-auto flex items-center gap-2 pr-2", {
                          invisible: isOpacityMode,
                        })}
                      >
                        <ChatExpandedToggleContainer />
                        <ChatSettingsOpenToggleContainer />
                        <ChatCloseButtonContainer />
                      </motion.div>
                    </AnimatePresence>
                  </ChatHeader>
                  <ChatMessagesContainer isOpacityMode={isOpacityMode} />
                </ChatBody>
                <ChatInputContainer show={showInput} />
                <ChatSearchContainer show={showSearch} />
                <MessageIsCopied fontSize={fontSize} show={isCopied} />
              </ChatWrapper>
            </motion.div>
          ) : (
            <ChatOpenButtonContainer />
          )}
        </AnimatePresence>
        <div className={clsx("relative", { hidden: isOpen || !notifyInRolledUpMode })}>
          <ToastContainer {...toastifyConfig} containerId="messages" />
        </div>
      </div>
      {isOpenSettings && <SettingsContainer />}
    </>
  );
}

export default ChatContainer;
